/**
 * FlightCard (Phase 6) — rich, reusable result card.
 *
 * Renders every field the spec requires: airline logo/name, flight number, departure &
 * arrival times (with +1 day marker), duration, origin/destination, stops + stop city,
 * cabin, baggage, refundable badge, seats-left urgency, save-amount badge, original
 * (strikethrough) & final price, and a Book Now button.
 *
 * @param {Object} props
 * @param {import('../../mock/types').Flight} props.flight
 * @param {(flight: import('../../mock/types').Flight) => void} props.onBook
 */
import React from "react";
import "./flight-card.css";

const formatPrice = (amount, currency = "PKR") =>
  `${currency} ${Math.round(amount).toLocaleString("en-PK")}`;

const stopsLabel = (flight) => {
  if (flight.stopCount === 0) return "Nonstop";
  const cities = flight.stopCities.join(", ");
  const noun = flight.stopCount === 1 ? "Stop" : "Stops";
  return `${flight.stopCount} ${noun}${cities ? ` (${cities})` : ""}`;
};

export default function FlightCard({ flight, onBook }) {
  const hasDiscount = flight.discount > 0;
  const lowSeats = flight.availableSeats <= 8;

  return (
    <article className="fc">
      {/* Left: airline + itinerary */}
      <div className="fc__main">
        <div className="fc__airline">
          <img
            src={flight.airlineLogo}
            alt={flight.airline}
            className="fc__logo"
            width={44}
            height={44}
          />
          <div className="fc__airline-meta">
            <span className="fc__airline-name">{flight.airline}</span>
            <span className="fc__flight-no">{flight.flightNumber}</span>
          </div>
        </div>

        <div className="fc__itinerary">
          <div className="fc__endpoint">
            <span className="fc__time">{flight.departureTime}</span>
            <span className="fc__airport">
              {flight.departureAirport.city} ({flight.departureAirport.code})
            </span>
          </div>

          <div className="fc__leg">
            <span className="fc__duration">{flight.duration}</span>
            <div className="fc__leg-line" aria-hidden="true">
              <span className="fc__leg-dot" />
              <span className="fc__leg-plane">
                <i className="fas fa-plane" />
              </span>
              <span className="fc__leg-dot" />
            </div>
            <span className={`fc__stops ${flight.stopCount === 0 ? "fc__stops--direct" : ""}`}>
              {stopsLabel(flight)}
            </span>
          </div>

          <div className="fc__endpoint fc__endpoint--right">
            <span className="fc__time">
              {flight.arrivalTime}
              {flight.arrivalDayOffset > 0 && (
                <sup className="fc__day-offset"> +{flight.arrivalDayOffset} Day</sup>
              )}
            </span>
            <span className="fc__airport">
              {flight.arrivalAirport.city} ({flight.arrivalAirport.code})
            </span>
          </div>
        </div>

        <div className="fc__tags">
          <span className="fc__tag">
            <i className="fas fa-chair" aria-hidden="true" /> {flight.cabinClass}
          </span>
          <span className="fc__tag">
            <i className="fas fa-suitcase-rolling" aria-hidden="true" /> {flight.baggageAllowance} Baggage
          </span>
          <span className={`fc__tag ${flight.refundable ? "fc__tag--ok" : "fc__tag--muted"}`}>
            <i className={`fas ${flight.refundable ? "fa-undo" : "fa-ban"}`} aria-hidden="true" />{" "}
            {flight.refundable ? "Refundable" : "Non-refundable"}
          </span>
          <span className="fc__tag">
            <i className="fas fa-plane" aria-hidden="true" /> {flight.aircraft}
          </span>
        </div>
      </div>

      {/* Right: pricing + CTA */}
      <div className="fc__aside">
        {lowSeats && (
          <span className="fc__seats">Only {flight.availableSeats} Seats Left</span>
        )}
        {hasDiscount && (
          <span className="fc__save">Save {formatPrice(flight.discount, flight.currency)}</span>
        )}
        <div className="fc__price-block">
          {hasDiscount && (
            <span className="fc__original">{formatPrice(flight.originalPrice, flight.currency)}</span>
          )}
          <span className="fc__final">{formatPrice(flight.finalPrice, flight.currency)}</span>
        </div>
        <button type="button" className="fc__book" onClick={() => onBook(flight)}>
          Book Now
        </button>
      </div>
    </article>
  );
}
